import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import globalTranslations from '../../../i18n/globalTranslations';
import { modelOf } from '../../../prop-types';
import ConfigStore from '../../../store/ConfigStore';
import ProductStore from '../../../store/ProductStore';
import RequestState from '../../../types/RequestState';
import { paramsToQueryIdentifier } from '../../../util/query';
import SimpleProductList from '../../product-list/SimpleProductList';
import ProductSlider from '../ProductSlider';

export const RECOMMENDED_PRODUCTS_LIST_ID = 'recommended_products';
export const RECOMMENDED_PRODUCTS_LIST_NAME = 'Recommended Products';

const RecommendedProductsSlider = ({
  configStore,
  productStore,
  ids,
  onRenderLabelRow,
  listId,
  listName,
  settings,
  overrideParams,
  cardSettings,
  productImageSettings,
  ...rest
}) => {
  const basicParams = {
    includeFilters: 0,
    includeChildProducts: '1',
    inStock: configStore.inStockProductsOnly ? '1' : '0',
    excludeContent: 1,
    optimizeResponse: 1,
  };

  const searchParams = overrideParams
    ? {
        ...basicParams,
        ...overrideParams,
      }
    : {
        ids,
        ...basicParams,
      };

  const getQueryIdentifier = () => {
    return paramsToQueryIdentifier(searchParams);
  };

  const getCurrentSearchResult = () => {
    return productStore.productQueryResults.get(getQueryIdentifier());
  };

  const getCurrentSearchState = () => {
    return productStore.productQueryStates.get(getQueryIdentifier());
  };

  if (
    !getCurrentSearchResult()?.total &&
    getCurrentSearchState() === RequestState.LOADED
  ) {
    return null;
  }

  const renderLabelRow = () => {
    if (onRenderLabelRow) {
      return onRenderLabelRow();
    }

    return (
      <label className="RecommendedProductsSlider__label">
        <FormattedMessage {...globalTranslations.recommendedProductsTitle} />
      </label>
    );
  };

  return (
    <div className="RecommendedProductsSlider">
      {renderLabelRow()}
      <SimpleProductList
        listId={listId}
        name={listName}
        searchParams={searchParams}
        listComponent={ProductSlider}
        allSections={true}
        settings={settings}
        cardSettings={cardSettings}
        productImageSettings={productImageSettings}
        {...rest}
      />
    </div>
  );
};

RecommendedProductsSlider.propTypes = {
  productStore: modelOf(ProductStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  ids: PropTypes.arrayOf(PropTypes.string),
  listId: PropTypes.string,
  listName: PropTypes.string,
  settings: PropTypes.object,
  cardSettings: PropTypes.object,
  productImageSettings: PropTypes.object,
  overrideParams: PropTypes.object,
  onRenderLabelRow: PropTypes.func,
};

RecommendedProductsSlider.defaultProps = {
  listId: RECOMMENDED_PRODUCTS_LIST_ID,
  listName: RECOMMENDED_PRODUCTS_LIST_NAME,
};

export default inject(
  'configStore',
  'productStore'
)(observer(RecommendedProductsSlider));
